<template>
  <div>
    <Details
      :data="data"
      :isPerTrip="isPerTrip"
      :calculationBasedAttribute="calculationBasedAttribute"
      :configRunSection="configRunSection"
      :disableFreightProvisionRetry="disableFreightProvisionRetry"
      @retry-freight-provision="$emit('retry-freight-provision')"
    />
    <div class="tablesection">
      <TableHeader
        :headers="conHeaderBasedOnType.headers"
        :toolTipMapping="conHeaderBasedOnType.toolTipMapping"
      />
      <TableRow
        v-for="consignee in consigneeData"
        :data="consignee"
        :key="consignee.id"
        :headers="conHeaderBasedOnType.headers"
      />
      <TableFooter
        v-if="isPerTrip"
        :data="consolidatedConsigneeData"
        :headers="conHeaderBasedOnType.headers"
      />
    </div>
    <div class="infocontainer" v-if="configRunSection">
      <svgicon name="info-blue" width="12" height="12" :original="true" />All consignments will be calculated by
      <!-- todo: deepika to add class-->
      <span style="text-transform: uppercase">{{configRunSection}}</span> only, due to client configuration.
    </div>
    <div v-for="(consignee, icx) in consigneeData" :key="icx">
      <MaterialDetails
        :configRunSection="configRunSection"
        :isPerTrip="isPerTrip"
        :consignee="consignee"
        :materialData="filterMaterialByConsignees(consignee)"
        :config="config" />
    </div>
  </div>
</template>
<script>
import { contractTypes } from '@/constants/order';
import { materialHeader, consigneeHeader } from '@/constants/freight-working';
import { isFTLIndentType, isPerCFT, isPerTon } from '@common/lib/common';
import TableHeader from '../../freight/Calculations/TableHeader';
import TableFooter from '../../freight/Calculations/TableFooter';
import Details from '../../freight/Calculations/Details';
import MaterialDetails from '../../freight/Calculations/MaterialDetails';
import TableRow from '../../freight/Calculations/TableRow';

export default {
  props: ['data', 'disableFreightProvisionRetry'],
  components: {
    TableHeader,
    TableFooter,
    Details,
    TableRow,
    MaterialDetails,
  },

  data() {
    return {
      contractTypes,
      materialHeader,
      consigneeHeader,
    };
  },
  computed: {
    configRunSection() {
      return this.data.config && this.data.config.capacity;
    },
    config() {
      return this.data.config;
    },
    conHeaderBasedOnType() {
      let headers = {};
      let toolTipMapping = {};
      if (this.isPerTrip) {
        headers = this.consigneeHeader.filter(h => h.id != 6);
        const index = headers.findIndex(h => h.id === 7);
        headers[
          index
        ].footerDesc = `Sum (${this.calculationBasedAttribute} * distance)`;
      } else {
        headers = this.consigneeHeader.filter(h => h.id != 3);
        let freightAmount = '';
        if (this.isPerCFT) {
          freightAmount =
            'Xw : Material Weight * Multiplying factor Xv: Material Volume';
        } else {
          freightAmount =
            'Xw : Material Weight Xv: Material Volume * Multiplying factor';
        }
        toolTipMapping = { freightAmount };
      }
      return { headers, toolTipMapping };
    },
    isFTL() {
      return isFTLIndentType(this.data.delivery_type);
    },
    isPerTrip() {
      return (
        this.isFTL &&
        (this.data && this.data.consignees.some(val => val.freight_unit === 1))
      );
    },
    // atleast one has per cft
    isPerCFT() {
      return (
        this.data && this.data.consignees.some(val => val.freight_unit === 4)
      );
    },
    consigneeList() {
      return _.uniqBy(this.data.consignees, 'id');
    },
    calculationBasedAttribute() {
      return this.configRunSection ? this.configRunSection : (this.data &&
            (this.data.capacityDiff.weight > this.data.capacityDiff.volume
              ? 'weight'
              : 'volume'));
    },
    consigneeData() {
      const consigneeList = _.uniqBy(this.data.consignees, 'id');
      return (
        consigneeList &&
        consigneeList.map((consignee) => {
          const data = this.calculateMaterialData(consignee.id);
          const obj = consignee;
          obj.contractRate = `${
            consignee.freight_charges
          } / ${this.getFreightUnit(consignee.freight_unit)}`;
          obj.distanceWithType = `${consignee.distance} KM`;
          obj.weight = data && data.weight;
          obj.volume = data && data.volume;

          if (this.isPerTrip) {
            const maxKey = this.calculationBasedAttribute;
            obj.freightAmount = obj[maxKey] * consignee.distance;
            obj.toolTipObj = {
              calculation: `(${
                this.calculationBasedAttribute
              } * distance / sum) *
              ${this.data.freight_rate ? 'updated charge' : 'freight'}`,
            };
          } else {
            let volume = obj.volume;
            let weight = obj.weight;
            if (isPerCFT(consignee.freight_unit)) {
              // For per CFT case
              weight = obj.weight * obj.multiplying_factor;
            } else if (isPerTon(consignee.freight_unit)) {
              weight = obj.weight / 1000;
              volume = obj.volume * obj.multiplying_factor;
            } else {
              volume = obj.volume * obj.multiplying_factor;
            }
            obj.greaterValue = weight > volume
              ? 'weight'
              : 'volume';
            obj.freightAmount = `Xw = ${weight} Xv = ${volume} =>
              X${obj.greaterValue.charAt(0)} is greater`;
            const freightRate = this.getFreightRate(consignee, data.rate);
            obj.freightRate = freightRate && Number(freightRate.toFixed(2));
            const materialData = this.data.materials.find(
              x => x.consignee_id === consignee.id,
            );
            if (this.checkFreightUnit(consignee.freight_unit) && materialData) {
              obj.toolTipObj = {
                calculation: `(${materialData.bifurcation_by} / total ${
                  materialData.bifurcation_by
                }) * charges}`,
              };
            }
            // obj.updated_charge = this.getUpdatedChargeByConsignee(consignee.id);
          }
          return obj;
        })
      );
    },
    consolidatedConsigneeData() {
      return {
        name: 'Final',
        contractRate: `${this.data.base_frieght.freight_charge} /
          ${this.getFreightUnit(this.data.base_frieght.freight_unit)}`,
        weight: this.data.capacity.weight.toFixed(2),
        volume: this.data.capacity.volume.toFixed(2),
        freightAmount: this.consigneeData.reduce(
          (accumulator, presentValue) => {
            accumulator += parseFloat(presentValue.freightAmount);
            return accumulator;
          },
          0,
        ).toFixed(2),
      };
    },
    contractPrice() {
      return this.data.materials.reduce((total, value) => {
        total += Number(value.contribution) || 0;
        return total;
      }, 0).toFixed(2);
    },
  },

  methods: {
    getFreightRate(consignee, rate) {
      let contractPrice = null;
      if (consignee.freight_unit === 1 || this.checkFreightUnit(consignee.freight_unit)) {
        contractPrice = rate;
      }
      return contractPrice;
    },
    getUpdatedChargeByConsignee(consigneeId) {
      const value = this.data.consignee_rate.find(
        val => val.id === consigneeId,
      );
      return value && value.total_amount;
    },
    checkFreightUnit(freightUnit) {
      return ![5, 6, 7].includes(freightUnit);
    },
    calculateMaterialData(consigneeId) {
      const materials = this.data.materials.length
        ? this.data.materials.filter(x => x.consignee_id === consigneeId)
        : [];
      const materialData = materials.reduce(
        (acc, current) => {
          acc.weight += current.weight;
          acc.volume += current.volume;
          acc.rate += current.contribution;
          return acc;
        },
        {
          weight: 0,
          volume: 0,
          rate: 0,
        },
      );
      return materialData;
    },
    filterMaterialByConsignees(consignee) {
      const materialsArray = this.data.materials.filter(
        x => x.consignee_id === consignee.id,
      );
      return (
        materialsArray &&
        materialsArray.map((material) => {
          const obj = {
            name: material.name,
            actual_quantity: material.quantity || 0,
            billed_quantity: material.quantity ? Math.ceil(material.quantity) : 0,
            volume: material.volume,
            weight: material.weight,
            costcenter: material.cost_center,
            sum: material.contribution ? material.contribution.toFixed(2) : 0,
            bifurcation_by: material.bifurcation_by,
          };
          if (this.isPerTrip) {
            let distance = consignee.distance;
            if (this.data.distance_final) {
              distance = this.data.distance_final;
            }
            obj.calculation = `((${
              material[this.calculationBasedAttribute]
            } * ${distance}
              / ${Number(this.consolidatedConsigneeData.freightAmount).toFixed(2)}) * ${
          this.contractPrice
        })`;
          } else if (this.checkFreightUnit(consignee.freight_unit)) {
            const rate = consignee.freightRate;
            obj.calculation = `((${material[material.bifurcation_by]} / ${
              this.calculateMaterialData(consignee.id)[material.bifurcation_by]
            })* ${rate})`;
          }
          return obj;
        })
      );
    },
    getFreightUnit(freightUnit) {
      const getFreight = this.contractTypes.find(
        type => type.id === freightUnit,
      );
      return getFreight ? getFreight.name : '-';
    },
  },
};
</script>
